import React, { useDeferredValue, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Shell from '../../components/Shell';
import CoverVisual from '../../components/CoverVisual';
import api from '../../services/api';
import { getPillarImage, pillarLabel } from '../../visuals/portalImagery';

type FundCard = {
  slug: string;
  name: string;
  pillar: string;
  status: string;
  image?: string;
  keyTerms: Record<string, string>;
  summaryExcerpt: string;
};

const PILLAR_ORDER = ['real_estate', 'technology', 'asset_lending'];

function matchesQuery(fund: FundCard, q: string) {
  if (!q) return true;
  const haystack = [
    fund.name,
    fund.slug,
    fund.status,
    fund.pillar,
    pillarLabel[fund.pillar] || '',
    fund.summaryExcerpt,
    ...Object.values(fund.keyTerms || {}),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export default function FundListPage() {
  const [funds, setFunds] = useState<FundCard[]>([]);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [pillarFilter, setPillarFilter] = useState<string>('all');
  const deferredQuery = useDeferredValue(query.trim().toLowerCase());

  useEffect(() => {
    api
      .get('/portal/funds')
      .then((r) => setFunds(r.data))
      .catch(() => setError('Could not load funds.'));
  }, []);

  const pillarsPresent = [
    ...PILLAR_ORDER.filter((p) => funds.some((f) => f.pillar === p)),
    ...Array.from(new Set(funds.map((f) => f.pillar))).filter(
      (p) => !PILLAR_ORDER.includes(p),
    ),
  ];

  const filtered = funds.filter((f) => {
    if (pillarFilter !== 'all' && f.pillar !== pillarFilter) return false;
    return matchesQuery(f, deferredQuery);
  });

  const grouped = filtered.reduce<Record<string, FundCard[]>>((acc, f) => {
    (acc[f.pillar] ||= []).push(f);
    return acc;
  }, {});

  const sectionOrder = [
    ...PILLAR_ORDER.filter((p) => grouped[p]?.length),
    ...Object.keys(grouped).filter((p) => !PILLAR_ORDER.includes(p)),
  ];

  const activeFilter = pillarFilter !== 'all' || deferredQuery.length > 0;

  return (
    <Shell title="Investor portal">
      <h1 className="font-display text-3xl text-ink-900">Fund slate</h1>
      <p className="mt-2 max-w-2xl text-sm text-ink-700">
        Review materials for funds open to soft interest. Pick a fund that
        matches what you want to underwrite—commitments are per vehicle, not to
        Consular.
      </p>
      {error && <p className="mt-4 text-sm text-red-700">{error}</p>}

      <div className="mt-8 space-y-4 border-y border-mist-200 bg-white/60 py-5">
        <label className="block max-w-xl text-sm text-ink-900">
          Search funds
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Name, geography, raise, partner…"
            className="mt-1 w-full border-ink-700/30"
            autoComplete="off"
          />
        </label>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-ink-700">
            Quick filter
          </p>
          <div
            className="mt-2 flex flex-wrap gap-2"
            role="group"
            aria-label="Filter by category"
          >
            <FilterChip
              active={pillarFilter === 'all'}
              onClick={() => setPillarFilter('all')}
              label="All"
              count={funds.length}
            />
            {pillarsPresent.map((p) => (
              <FilterChip
                key={p}
                active={pillarFilter === p}
                onClick={() => setPillarFilter(p)}
                label={pillarLabel[p] || p}
                count={funds.filter((f) => f.pillar === p).length}
              />
            ))}
          </div>
        </div>
        <p className="text-xs text-ink-700/80">
          Showing {filtered.length} of {funds.length}
          {activeFilter ? ' matching' : ''}
          {activeFilter && (
            <>
              {' · '}
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setPillarFilter('all');
                }}
                className="underline hover:text-ink-900"
              >
                Clear
              </button>
            </>
          )}
        </p>
      </div>

      <div className="mt-10 space-y-14">
        {sectionOrder.length === 0 && !error && (
          <p className="text-sm text-ink-700">
            No funds match that search or filter.
          </p>
        )}
        {sectionOrder.map((pillar) => (
          <section key={pillar} className="animate-fade-up">
            <CoverVisual
              pillar={pillar}
              image={getPillarImage(pillar)}
              alt={pillarLabel[pillar] || pillar}
              variant="pillar"
              label={pillarLabel[pillar] || pillar}
            />
            <ul className="mt-0 divide-y divide-mist-200 border-b border-mist-200">
              {grouped[pillar].map((f) => (
                <li key={f.slug}>
                  <Link
                    to={`/portal/funds/${f.slug}`}
                    className="group flex gap-4 py-5 transition hover:bg-white/70 sm:gap-6"
                  >
                    <CoverVisual
                      pillar={f.pillar}
                      slug={f.slug}
                      image={f.image}
                      alt=""
                      variant="thumb"
                      className="transition duration-500 group-hover:brightness-110"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <h3 className="font-display text-xl text-ink-900 group-hover:underline">
                          {f.name}
                        </h3>
                        <span className="text-xs uppercase tracking-wide text-ink-700">
                          {f.status}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-ink-700">
                        {f.summaryExcerpt}
                        {f.summaryExcerpt.length >= 280 ? '…' : ''}
                      </p>
                      <p className="mt-2 text-xs text-ink-700/80">
                        {[f.keyTerms.targetRaise, f.keyTerms.minimum]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Shell>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        active
          ? 'border border-navy-900 bg-navy-900 px-3 py-1.5 text-sm text-mist-50'
          : 'border border-mist-200 bg-white px-3 py-1.5 text-sm text-ink-700 hover:border-navy-700/40 hover:text-ink-900'
      }
    >
      {label}
      <span className={active ? 'ml-2 text-mist-200' : 'ml-2 text-ink-700/60'}>
        {count}
      </span>
    </button>
  );
}
